import React, { useState, useRef } from 'react';
import { Point2D, CourtSurface, ShotRecommendation, PlayerPositionState } from '../../core/types';
import { tennisAudio } from '../../core/audio';

interface TennisCourtCanvasProps {
  surface: CourtSurface;
  state: PlayerPositionState;
  onUpdateState: (next: PlayerPositionState) => void;
  selectedShot: ShotRecommendation | null;
  onSelectShot: (shot: ShotRecommendation) => void;
}

export const TennisCourtCanvas: React.FC<TennisCourtCanvasProps> = ({
  surface,
  state,
  onUpdateState,
  selectedShot,
  onSelectShot,
}) => {
  const containerRef = useRef<SVGSVGElement | null>(null);
  const [isDraggingBall, setIsDraggingBall] = useState(false);

  // Surface color schemes
  const themes = {
    clay: {
      outer: '#9a3412',
      inner: '#c2410c',
      lines: '#ffffff',
      alley: '#b45309',
      net: '#ffffff',
      netPost: '#451a03',
      name: '法网红土 (Roland Garros Red Clay)',
    },
    grass: {
      outer: '#14532d',
      inner: '#15803d',
      lines: '#ffffff',
      alley: '#166534',
      net: '#ffffff',
      netPost: '#052e16',
      name: '温网草地 (Wimbledon Lawn)',
    },
    hardcourt_us: {
      outer: '#1e3a8a',
      inner: '#0284c7',
      lines: '#ffffff',
      alley: '#0369a1',
      net: '#ffffff',
      netPost: '#0f172a',
      name: '美网经典蓝 (DecoTurf Midnight & Sky)',
    },
    hardcourt_ao: {
      outer: '#0369a1',
      inner: '#06b6d4',
      lines: '#ffffff',
      alley: '#0891b2',
      net: '#ffffff',
      netPost: '#0f172a',
      name: '澳网极速蓝 (Pacific Blue)',
    },
    night_session: {
      outer: '#090d16',
      inner: '#1e293b',
      lines: '#ccff00',
      alley: '#172554',
      net: '#94a3b8',
      netPost: '#000000',
      name: '碳纤夜场巡回赛 (Carbon Night Pro)',
    },
  };

  const theme = themes[surface] || themes.clay;

  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDraggingBall(true);
    handleCourtPointer(e);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (isDraggingBall) {
      handleCourtPointer(e);
    }
  };

  const handlePointerUp = () => {
    if (isDraggingBall) {
      setIsDraggingBall(false);
      tennisAudio.playBounce();
    }
  };

  const handleCourtPointer = (e: React.PointerEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(8, Math.min(92, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(54, Math.min(96, ((e.clientY - rect.top) / rect.height) * 100)); // Keep on user's half

    onUpdateState({
      ...state,
      ball: { x, y },
      playerA: { x: Math.max(12, Math.min(88, x)), y: Math.min(97, y + 3) },
    });
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto flex flex-col items-center select-none">
      {/* Court Header & Quick Surface Info */}
      <div className="w-full flex items-center justify-between px-2 py-1 mb-1 text-xs text-slate-300">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: theme.inner }}></span>
          <span className="font-semibold text-slate-200">{theme.name}</span>
        </div>
        <div className="text-[11px] text-amber-400/90 font-medium">
          点击/拖拽网球重置击球点
        </div>
      </div>

      {/* SVG Tennis Court Arena */}
      <div className="relative w-full aspect-[1/1.6] max-h-[68vh] rounded-2xl overflow-hidden shadow-2xl border-4 border-slate-800/90 bg-slate-950">
        <svg
          ref={containerRef}
          viewBox="0 0 100 160"
          className="w-full h-full cursor-crosshair touch-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <defs>
            {/* Net gradient */}
            <linearGradient id="netStrap" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#cbd5e1" />
            </linearGradient>

            {/* Ball Glow Filter */}
            <filter id="ballFilter" x="-40%" y="-40%" width="180%" height="180%">
              <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="#ccff00" floodOpacity="0.8" />
            </filter>

            {/* Target Ring Glow */}
            <filter id="targetFilter">
              <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="#10b981" floodOpacity="0.9" />
            </filter>
          </defs>

          {/* 1. Surrounding Out-of-bounds Court Apron */}
          <rect x="0" y="0" width="100" height="160" fill={theme.outer} />

          {/* 2. Doubles In-bounds Court: x: 12 to 88, y: 12 to 148 */}
          <rect x="12" y="12" width="76" height="136" fill={theme.alley} />

          {/* 3. Singles In-bounds Court: x: 22 to 78, y: 12 to 148 */}
          <rect x="22" y="12" width="56" height="136" fill={theme.inner} />

          {/* 4. Court Boundary Lines */}
          {/* Doubles Sidelines */}
          <line x1="12" y1="12" x2="12" y2="148" stroke={theme.lines} strokeWidth="0.8" />
          <line x1="88" y1="12" x2="88" y2="148" stroke={theme.lines} strokeWidth="0.8" />

          {/* Singles Sidelines */}
          <line x1="22" y1="12" x2="22" y2="148" stroke={theme.lines} strokeWidth="0.8" />
          <line x1="78" y1="12" x2="78" y2="148" stroke={theme.lines} strokeWidth="0.8" />

          {/* Baselines */}
          <line x1="12" y1="12" x2="88" y2="12" stroke={theme.lines} strokeWidth="1.2" />
          <line x1="12" y1="148" x2="88" y2="148" stroke={theme.lines} strokeWidth="1.2" />

          {/* Center Baseline Marks */}
          <line x1="50" y1="12" x2="50" y2="15" stroke={theme.lines} strokeWidth="0.8" />
          <line x1="50" y1="145" x2="50" y2="148" stroke={theme.lines} strokeWidth="0.8" />

          {/* Service Lines: y = 46 (Top), y = 114 (Bottom) */}
          <line x1="22" y1="46" x2="78" y2="46" stroke={theme.lines} strokeWidth="0.8" />
          <line x1="22" y1="114" x2="78" y2="114" stroke={theme.lines} strokeWidth="0.8" />

          {/* Center Service Line: from y = 46 to y = 114 */}
          <line x1="50" y1="46" x2="50" y2="114" stroke={theme.lines} strokeWidth="0.8" />

          {/* Service Box Watermark Text */}
          <text x="36" y="65" fill={theme.lines} fillOpacity="0.2" fontSize="5" fontWeight="bold" textAnchor="middle">
            AD COURT
          </text>
          <text x="64" y="65" fill={theme.lines} fillOpacity="0.2" fontSize="5" fontWeight="bold" textAnchor="middle">
            DEUCE
          </text>
          <text x="36" y="98" fill={theme.lines} fillOpacity="0.2" fontSize="5" fontWeight="bold" textAnchor="middle">
            DEUCE
          </text>
          <text x="64" y="98" fill={theme.lines} fillOpacity="0.2" fontSize="5" fontWeight="bold" textAnchor="middle">
            AD COURT
          </text>

          {/* Joy Tennis Club Center Court Emblem */}
          <circle cx="50" cy="80" r="7" fill="none" stroke={theme.lines} strokeOpacity="0.15" strokeWidth="0.5" />
          <text x="50" y="81.5" fill={theme.lines} fillOpacity="0.3" fontSize="2.5" fontWeight="black" textAnchor="middle" letterSpacing="0.5">
            JOY TENNIS
          </text>

          {/* 5. The Tennis Net: y = 80 */}
          {/* Net shadow */}
          <line x1="8" y1="81" x2="92" y2="81" stroke="#000000" strokeOpacity="0.4" strokeWidth="2" />
          {/* Net Mesh Pattern */}
          <line x1="9" y1="80" x2="91" y2="80" stroke={theme.net} strokeWidth="1.8" strokeDasharray="0.8 0.4" />
          {/* Net Top White Band */}
          <line x1="9" y1="79.8" x2="91" y2="79.8" stroke="#ffffff" strokeWidth="0.9" />
          {/* Net Posts */}
          <circle cx="9" cy="80" r="1.8" fill={theme.netPost} stroke="#ffffff" strokeWidth="0.4" />
          <circle cx="91" cy="80" r="1.8" fill={theme.netPost} stroke="#ffffff" strokeWidth="0.4" />
          {/* Net Center White Strap */}
          <rect x="49" y="78.8" width="2" height="2.4" fill="url(#netStrap)" rx="0.3" />

          {/* 6. Active Shot Trajectory (Bézier Arc) */}
          {selectedShot && (
            <g>
              {/* Ground target shadow / drop landing circle */}
              {(() => {
                const tx = (selectedShot.targetPoint.x / 100) * 80 + 10;
                const ty = (selectedShot.targetPoint.y / 100) * 160;
                const bx = (state.ball.x / 100) * 80 + 10;
                const by = (state.ball.y / 100) * 160;
                const midX = (bx + tx) / 2;
                const midY = 80;

                return (
                  <>
                    {/* Target Bullseye */}
                    <circle cx={tx} cy={ty} r="7" fill="#10b981" fillOpacity="0.25" filter="url(#targetFilter)" />
                    <circle cx={tx} cy={ty} r="4" fill="#10b981" fillOpacity="0.6" />
                    <circle cx={tx} cy={ty} r="1.5" fill="#ffffff" />
                    <text x={tx} y={ty - 8} fill="#10b981" fontSize="3" fontWeight="bold" textAnchor="middle">
                      TARGET 🎯
                    </text>

                    {/* Flight Path (Animated Dash) */}
                    <path
                      d={`M ${bx} ${by} Q ${midX} ${midY} ${tx} ${ty}`}
                      fill="none"
                      stroke={selectedShot.spinType === 'topspin' ? '#ccff00' : selectedShot.spinType === 'slice' ? '#38bdf8' : '#f59e0b'}
                      strokeWidth="1.8"
                      strokeDasharray="3 2"
                      strokeLinecap="round"
                    >
                      <animate attributeName="stroke-dashoffset" from="10" to="0" dur="0.8s" repeatCount="indefinite" />
                    </path>

                    {/* Net Clearance Badge at Net */}
                    <rect x={midX - 12} y="76" width="24" height="7" rx="3.5" fill="#090d16" stroke="#ccff00" strokeWidth="0.6" />
                    <text x={midX} y="80.6" fill="#ccff00" fontSize="2.5" fontWeight="black" textAnchor="middle">
                      过网 {selectedShot.netClearanceFeet}ft
                    </text>

                    {/* Spin RPM & Magnus Aero Indicator Badge along path */}
                    <g transform={`translate(${(bx * 2 + tx) / 3}, ${(by * 2 + ty) / 3})`}>
                      <rect x="-14" y="-4" width="28" height="8" rx="4" fill="#020617" stroke="#38bdf8" strokeWidth="0.5" />
                      <text x="0" y="1.5" fill="#38bdf8" fontSize="2.4" fontWeight="bold" textAnchor="middle">
                        🌀 {selectedShot.rpm} RPM
                      </text>
                    </g>

                    {/* Secondary Bounce Height Projection Marker (落地反弹高度) */}
                    <g transform={`translate(${tx}, ${ty - 11})`}>
                      <rect
                        x="-16"
                        y="-4.5"
                        width="32"
                        height="9"
                        rx="4.5"
                        fill="#0f172a"
                        stroke={selectedShot.spinType === 'topspin' ? '#f59e0b' : selectedShot.spinType === 'slice' ? '#06b6d4' : '#10b981'}
                        strokeWidth="0.6"
                      />
                      <text
                        x="0"
                        y="1.5"
                        fill={selectedShot.spinType === 'topspin' ? '#f59e0b' : selectedShot.spinType === 'slice' ? '#06b6d4' : '#10b981'}
                        fontSize="2.4"
                        fontWeight="black"
                        textAnchor="middle"
                      >
                        弹起 {selectedShot.bounceHeightMeters}m {selectedShot.spinType === 'topspin' ? '▲ 压肩' : selectedShot.spinType === 'slice' ? '▼ 贴地' : '• 标准'}
                      </text>
                    </g>
                  </>
                );
              })()}
            </g>
          )}

          {/* 7. Opponent Tokens (Top Half) */}
          <g transform={`translate(${(state.opponent1.x / 100) * 80 + 10}, ${(state.opponent1.y / 100) * 160})`}>
            <circle cx="0" cy="0" r="4.2" fill="#ef4444" stroke="#ffffff" strokeWidth="0.8" />
            <text x="0" y="1.2" fill="#ffffff" fontSize="3" fontWeight="bold" textAnchor="middle">
              对手
            </text>
          </g>

          {state.gameMode === 'doubles' && state.opponent2 && (
            <g transform={`translate(${(state.opponent2.x / 100) * 80 + 10}, ${(state.opponent2.y / 100) * 160})`}>
              <circle cx="0" cy="0" r="4.2" fill="#f97316" stroke="#ffffff" strokeWidth="0.8" />
              <text x="0" y="1.2" fill="#ffffff" fontSize="3" fontWeight="bold" textAnchor="middle">
                对手2
              </text>
            </g>
          )}

          {/* 8. Doubles Partner (Bottom Half) */}
          {state.gameMode === 'doubles' && state.partnerA && (
            <g transform={`translate(${(state.partnerA.x / 100) * 80 + 10}, ${(state.partnerA.y / 100) * 160})`}>
              <circle cx="0" cy="0" r="4.2" fill="#3b82f6" stroke="#ffffff" strokeWidth="0.8" />
              <text x="0" y="1.2" fill="#ffffff" fontSize="3" fontWeight="bold" textAnchor="middle">
                搭档
              </text>
            </g>
          )}

          {/* 9. User Player Token (Bottom Half) */}
          <g transform={`translate(${(state.playerA.x / 100) * 80 + 10}, ${(state.playerA.y / 100) * 160})`}>
            <circle cx="0" cy="0" r="4.8" fill="#0284c7" stroke="#38bdf8" strokeWidth="1" />
            <text x="0" y="1.2" fill="#ffffff" fontSize="2.8" fontWeight="black" textAnchor="middle">
              YOU
            </text>
          </g>

          {/* 10. Glowing Tennis Ball (Interactive Draggable) */}
          <g
            transform={`translate(${(state.ball.x / 100) * 80 + 10}, ${(state.ball.y / 100) * 160})`}
            className="transition-transform duration-75 cursor-grab active:cursor-grabbing"
            filter="url(#ballFilter)"
          >
            <circle cx="0" cy="0" r="3.6" fill="#ccff00" stroke="#84cc16" strokeWidth="0.5" />
            {/* Seams */}
            <path d="M -2.2 -1.5 C -1 0, -1 1.5, -2.2 2.2" fill="none" stroke="#ffffff" strokeWidth="0.5" />
            <path d="M 2.2 -1.5 C 1 0, 1 1.5, 2.2 2.2" fill="none" stroke="#ffffff" strokeWidth="0.5" />
          </g>
        </svg>
      </div>
    </div>
  );
};
